import { cn } from "cn";
import { BadgeDot } from "@/components/ui/badge/badge-dot";
import { Button } from "@/components/ui/button/button";
import {
  BwLoadingLine,
  BwQuestionMarkCircleLine,
} from "@/components/ui/icons/icons.generated";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { LineItemCard, PromoCode } from "@/features/my/checkout/shell";
import {
  intervalSuffix,
  type PurchaseIntentPriceDetail,
  SummaryRow,
} from "@/features/shared/checkout-summary";
import type { PresaleStandardPrice } from "../../_api/use-presale-standard-price";

type PresaleSummaryCardProps = {
  /**
   * The platform's ledger, off `payload.priceDetail`. `undefined` only for a reservation
   * whose payload predates the field — the page owns the loading state and does not render
   * this card until the reservation has arrived.
   */
  priceDetail: PurchaseIntentPriceDetail | undefined;
  launchDateLabel: string;
  /** The launch date with its year — the schedule row states a real billing date. */
  launchDateWithYearLabel: string;
  /**
   * The plan's catalog price, i.e. what is charged once the market launches. `null`
   * while it loads or when it cannot be resolved — the row then states the date
   * without an amount rather than implying the $0 presale rate continues.
   */
  standardPrice: PresaleStandardPrice | null;
  onConfirm: () => void;
  isConfirming: boolean;
  disabled: boolean;
  className?: string;
};

/**
 * The presale twin of the checkout's `CheckoutCard`, on the same server-priced ledger.
 *
 * Every money row here is a label the platform computed (`price.service.ts`); nothing is
 * summed in the browser. The promo field is the member checkout's own `PromoCode`, which
 * writes the code to the intent and lets the server re-price.
 */
export function PresaleSummaryCard({
  priceDetail,
  launchDateLabel,
  launchDateWithYearLabel,
  standardPrice,
  onConfirm,
  isConfirming,
  disabled,
  className,
}: PresaleSummaryCardProps) {
  // `discount.totalLabel` is set only when the cart discount is greater than zero
  // (price.service.ts), so its presence IS the "an offer applied" signal.
  const discountTotalLabel = priceDetail?.discount?.totalLabel;
  const hasDiscount = Boolean(discountTotalLabel);
  const isDueTodayFree = (priceDetail?.dueToday ?? 0) === 0;

  return (
    <div
      className={cn(
        /* sorted */
        "w-full",
        "py-4 pt-2",
        "shadow-lg border border-border-dark-min rounded-2xl",
        "md:w-90",
        className,
      )}
    >
      <div className="divide-y divide-border-dark-min border-b-2 border-border-dark-min border-dashed">
        <div className="px-4 pb-1 divide-y divide-border-dark-min">
          {priceDetail?.lineItems?.map((lineItem) => (
            <LineItemCard
              key={lineItem.name}
              lineItem={lineItem}
              recurringCaption={`Billed monthly from ${launchDateLabel}`}
            />
          ))}
        </div>
        <PromoCode />
        {/* The ledger. Every row either adds or subtracts, top to bottom, and lands on
            Due today — so the amount on the button is arithmetic the buyer can check.
            It closes by construction now: the platform sums EVERY line into `subTotal`
            and computes `dueToday = subTotal − discount + tax + processingFee`, which is
            why the convenience fee needs a row of its own here. The card used to subtotal
            the one-time lines alone, to make the same identity hold against a discount
            it had computed itself. */}
        <div className="py-2">
          <SummaryRow
            className="py-1"
            label="Subtotal"
            value={priceDetail?.subTotalLabel}
          />
          {hasDiscount && (
            <SummaryRow
              className="py-1"
              label="Offer discount"
              tone="success"
              value={`-${discountTotalLabel}`}
            />
          )}
          {/* The server nulls this label when the fee is zero, so the row's absence
              is the intended rendering of "no fee" — never a $0 line. */}
          {priceDetail?.processingFeeLabel && (
            <SummaryRow
              className="py-1"
              label={
                <span className="flex items-center gap-1">
                  Convenience fee
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <BwQuestionMarkCircleLine
                        className="text-text-dark-min cursor-help"
                        size={16}
                      />
                    </TooltipTrigger>
                    <TooltipContent>Credit card processing fee</TooltipContent>
                  </Tooltip>
                </span>
              }
              value={priceDetail.processingFeeLabel}
            />
          )}
        </div>
      </div>
      <SummaryRow
        caption={hasDiscount ? `You saved ${discountTotalLabel}` : undefined}
        emphasis
        label="Due today"
        value={priceDetail?.dueTodayLabel}
      />
      {/* Deliberately NOT `priceDetail.nextPaymentDate` / `nextPaymentLabel`. The platform
          sets those to today + 1 month at the $0.00 presale rate (price.service.ts), i.e.
          "your next payment is $0.00 next month" — the opposite of what this campaign
          promises. The launch date and the catalog price are the only sources that can
          state when billing really starts, and at what. */}
      <div className="px-1">
        <div className="rounded-xl p-3 bg-bg-dark-min">
          <div className="flex items-center gap-1 text-xs">
            <div className="flex items-center justify-center w-4">
              <BadgeDot />
            </div>
            <p className="grow text-text-dark-high">
              Next payment · {launchDateWithYearLabel}
            </p>
            {standardPrice?.formattedPrice && (
              <p>
                {standardPrice.formattedPrice}
                {intervalSuffix(standardPrice.interval)}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="px-4 pt-3">
        <Button
          disabled={disabled || isConfirming}
          className="w-full"
          onClick={onConfirm}
          size="xl"
        >
          {isConfirming ? (
            <>
              <BwLoadingLine size={20} className="animate-spin" />
              Processing...
            </>
          ) : (
            `Confirm & Reserve${isDueTodayFree ? "" : ` ${priceDetail?.dueTodayLabel}`}`
          )}
        </Button>
      </div>
      <p className="px-4 pt-3 text-xs leading-4 text-text-dark-low">
        Your membership doesn’t begin billing until {launchDateWithYearLabel},
        when service launches in your area.
      </p>
    </div>
  );
}
